import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import buttons from 'src/lib/ui/config/buttons';
import { Feature } from 'src/replicant/analytics';
import { assertNever } from 'src/replicant/utils';

export type BuddiesInviteStatus =
  | 'not-invited'
  | 'invite-sent'
  | 'invite-received'
  | 'in-party'
  | 'suggestion';

export type BuddiesInviteItemParams =
  | {
      id: string;
      name: string;
      photo: string;
      status: BuddiesInviteStatus;
      score?: number;
      onAction?: () => Promise<void>;
      onSecondaryAction?: () => Promise<void>;
    }
  | {
      title: string;
      message?: string;
    };

export class BuddiesFriendEntry extends View {
  private base: ImageScaleView;
  private avatar: Avatar;
  private nameLabel: LangBitmapFontTextView;
  private titleLabel: LangBitmapFontTextView;
  private messageLabel: LangBitmapFontTextView;
  private buttonOk: ButtonScaleViewWithText;
  private buttonCancel: ButtonScaleViewWithText;
  private iconCancel: ImageScaleView;
  private scoreLabel: LangBitmapFontTextView;

  constructor(opts?: ViewOpts) {
    super({
      width: 570,
      height: 80,
      ...opts,
    });

    // Player row base
    this.base = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/buddies/common/friend_base.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    this.avatar = new Avatar({
      superview: this,
      width: 60,
      height: 60,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    this.nameLabel = new LangBitmapFontTextView({
      superview: this,
      x: 90,
      y: 0,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: this.style.width - 300,
      height: this.style.height,
      color: '#9A7171',
    });

    this.scoreLabel = new LangBitmapFontTextView({
      superview: this,
      x: this.style.width - 230,
      y: 0,
      align: 'right',
      verticalAlign: 'center',
      size: 20,
      font: 'Body',
      width: 80,
      height: this.style.height,
      color: '#BF8147',
    });

    this.titleLabel = new LangBitmapFontTextView({
      superview: this,
      align: 'center',
      verticalAlign: 'center',
      size: 36,
      font: 'Body',
      width: this.style.width - 40,
      height: this.style.height,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
      color: '#9C693A',
      centerOnOrigin: true,
    });

    // Info/message text
    this.messageLabel = new LangBitmapFontTextView({
      superview: this,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5 + 25,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 18,
      font: 'Body',
      width: this.style.width - 40,
      height: this.style.height * 0.5,
      color: '#9C693A',
    });

    // Primary action button (Invite / Accept / Cancel)
    this.buttonOk = new ButtonScaleViewWithText({
      superview: this.base,
      ...buttons.secondary,
      width: 120,
      height: 60,
      fontSize: 20,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: this.base.style.width - 75,
      y: this.base.style.height * 0.5,
      font: 'Body',
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_START,
      },
    });

    // Secondary action button (Reject / Dismiss)
    this.buttonCancel = new ButtonScaleViewWithText({
      superview: this.base,
      ...buttons.cancel,
      width: 60,
      height: 55,
      fontSize: 22,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: this.base.style.width - 170,
      y: this.base.style.height * 0.5,
      font: 'Body',
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_START,
      },
    });

    this.iconCancel = new ImageScaleView({
      superview: this.buttonCancel,
      width: 25,
      height: 25,
      x: this.buttonCancel.style.width * 0.5,
      y: this.buttonCancel.style.height * 0.5 - 3,
      scaleMethod: 'contain',
      centerOnOrigin: true,
      image: 'assets/ui/match2/common/icon_cross.png',
    });
  }

  public setup(params: BuddiesInviteItemParams) {
    if ('title' in params) {
      this.showTitle(params.title, params.message);
      return;
    }

    this.showPlayer(params);
  }

  private showTitle(title: string, message?: string) {
    this.titleLabel.localeText = () => title;
    this.titleLabel.show();
    this.titleLabel.updateOpts({
      y: this.style.height * 0.5 - (message ? 10 : 0),
    });

    if (message) {
      this.messageLabel.localeText = () => message;
      this.messageLabel.show();
    } else {
      this.messageLabel.hide();
    }

    this.nameLabel.hide();
    this.base.hide();
    this.buttonOk.hide();
    this.buttonCancel.hide();
    this.avatar.hide();
    this.scoreLabel.hide();
  }

  private showPlayer(params: Extract<BuddiesInviteItemParams, { id: string }>) {
    this.nameLabel.show();
    this.base.show();
    this.avatar.show();
    this.titleLabel.hide();
    this.messageLabel.hide();

    this.nameLabel.localeText = () => params.name;
    this.avatar.updateImage(params.photo);

    this.buttonOk.onClick = async () => params.onAction?.();
    this.buttonCancel.onClick = async () => params.onSecondaryAction?.();

    switch (params.status) {
      case 'in-party':
        this.buttonOk.hide();
        this.buttonCancel.hide();
        if (params.score !== undefined) {
          this.scoreLabel.localeText = () => `${params.score}`;
          this.scoreLabel.show();
        } else {
          this.scoreLabel.hide();
        }
        break;
      case 'invite-received':
        this.buttonOk.show();
        this.buttonOk.localeText = () =>
          i18n('match2.buddies.invitePopup.inviteAccept');
        this.buttonCancel.show();
        this.buttonCancel.localeText = () => '';
        this.iconCancel.show();
        this.scoreLabel.hide();
        break;
      case 'not-invited':
        this.buttonOk.show();
        this.buttonOk.localeText = () =>
          i18n('match2.buddies.invitePopup.invite');
        this.buttonCancel.hide();
        this.scoreLabel.hide();
        break;
      case 'suggestion':
        this.buttonOk.show();
        this.buttonOk.localeText = () =>
          i18n('match2.buddies.invitePopup.invite');
        this.buttonCancel.show();
        this.buttonCancel.localeText = () => '';
        this.iconCancel.show();
        this.scoreLabel.hide();
        break;
      case 'invite-sent':
        this.buttonOk.show();
        this.buttonOk.localeText = () =>
          i18n('match2.buddies.invitePopup.inviteCancel');
        this.buttonCancel.hide();
        this.scoreLabel.hide();
        break;
      default:
        assertNever(params.status);
    }
  }
}
